/**
 * @voltxampere/engine — Transient stamps that delegate to DC
 *
 * Linear non-storage components (resistor, ground, V/I sources) are
 * time-invariant — their transient stamp is *exactly* the DC stamp.
 * Same for the diode: its companion model is re-linearised every
 * Newton iteration, and the transient solver runs Newton inside
 * each step, so the DC stamp produces the right result.
 *
 * `DCStampFn` is contravariantly assignable to `TransientStampFn`
 * (the transient context extends the DC one), so we can register
 * the DC functions directly in the transient dispatch table — the
 * extra `h`, `method`, `xPrevPrev`, `firstStep`, `previousCurrent`
 * fields are simply ignored.
 *
 * Source stamps multiply their value by `ctx.sourceScale`. The
 * transient solver always passes `sourceScale = 1` (the §3.7
 * source-stepping fallback is a DC convergence aid only), so the
 * sources behave as full-value DC sources at every transient step.
 * Sprint 7+ will add time-dependent sources (pulse / sine / PWL)
 * with bespoke transient stamps that sit alongside these.
 *
 * Placeholder semiconductor stamps (BJT, MOSFET, op-amp) reuse the
 * DC throwing variants — they're not implemented in DC or transient
 * for Sprint 6.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { resistorDCStamp, voltageSourceDCStamp, currentSourceDCStamp, groundDCStamp, } from "../linear/index.js";
import { diodeDCStamp, ledDCStamp, zenerDCStamp, jfetNDCStamp, jfetPDCStamp, bjtNpnDCStamp, bjtPnpDCStamp, mosfetNmosDCStamp, mosfetPmosDCStamp, opampDCStamp, } from "../nonlinear/index.js";
import { defineTransientStamp } from "./types.js";
// Direct re-exports — DCStampFn is assignable to TransientStampFn
// because TransientStampContext extends DCStampContext.
export const resistorTransientStamp = resistorDCStamp;
export const voltageSourceTransientStamp = voltageSourceDCStamp;
export const currentSourceTransientStamp = currentSourceDCStamp;
export const groundTransientStamp = groundDCStamp;
export const diodeTransientStamp = diodeDCStamp;
// Sprint 23 B1 — LED transient stamp delegates to LED DC stamp
// (Newton re-linearisation per step, identical to diode pattern).
export const ledTransientStamp = ledDCStamp;
// Sprint 23 B2 — Zener transient stamp delegates to Zener DC stamp
// (both forward Shockley and reverse breakdown branches).
export const zenerTransientStamp = zenerDCStamp;
// Sprint 23 B3 — JFET transient stamps delegate to JFET DC stamps.
export const jfetNTransientStamp = jfetNDCStamp;
export const jfetPTransientStamp = jfetPDCStamp;
export const bjtTransientStamp = bjtNpnDCStamp;
export const bjtPnpTransientStamp = bjtPnpDCStamp;
export const mosfetTransientStamp = mosfetNmosDCStamp;
export const mosfetPmosTransientStamp = mosfetPmosDCStamp;
export const opampTransientStamp = opampDCStamp;
export const resistorTransientStampDefinition = defineTransientStamp("resistor", resistorTransientStamp, []);
export const voltageSourceTransientStampDefinition = defineTransientStamp("voltage-source", voltageSourceTransientStamp, ["vsource"]);
export const currentSourceTransientStampDefinition = defineTransientStamp("current-source", currentSourceTransientStamp, []);
export const groundTransientStampDefinition = defineTransientStamp("ground", groundTransientStamp, []);
export const diodeTransientStampDefinition = defineTransientStamp("diode", diodeTransientStamp, []);
// Sprint 23 B1 — LED transient stamp registration.
export const ledTransientStampDefinition = defineTransientStamp("led", ledTransientStamp, []);
// Sprint 23 B2 — Zener transient stamp registration.
export const zenerTransientStampDefinition = defineTransientStamp("zener", zenerTransientStamp, []);
// Sprint 23 B3 — JFET transient stamp registrations.
export const jfetNTransientStampDefinition = defineTransientStamp("jfet-n", jfetNTransientStamp, []);
export const jfetPTransientStampDefinition = defineTransientStamp("jfet-p", jfetPTransientStamp, []);
// Sprint 7 BJT / MOSFET split: each polarity gets its own transient
// registration delegating to the real DC stamp (Ebers-Moll /
// Shichman-Hodges stamps are reused directly because the
// linearisation happens per Newton iteration).
export const bjtTransientStampDefinition = defineTransientStamp("bjt-npn", bjtTransientStamp, []);
export const bjtPnpTransientStampDefinition = defineTransientStamp("bjt-pnp", bjtPnpTransientStamp, []);
export const mosfetTransientStampDefinition = defineTransientStamp("mosfet-nmos", mosfetTransientStamp, []);
export const mosfetPmosTransientStampDefinition = defineTransientStamp("mosfet-pmos", mosfetPmosTransientStamp, []);
export const opampTransientStampDefinition = defineTransientStamp("opamp", opampTransientStamp, []);
//# sourceMappingURL=passthrough.js.map